import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import type { AccountInfo, IPublicClientApplication } from '@azure/msal-browser';
import type { AuthUser, AppRole } from '../types/auth';
import { OFFICE_ROLES } from '../types/auth';
import { loginRequest, graphScopes, getRolesFromAccount } from './authConfig';
import { acquireToken } from './tokens';

/**
 * Acquire a Microsoft Graph token: silent first, then the shared popup (`./tokens`).
 *
 * A plain function over the (stable) MSAL instance, so a Graph client can be created once per
 * sign-in without holding the hook's per-render `getGraphToken` in a ref (13-08, ISS-185).
 * `account` defaults to the first cached account, the same one `useAuth()` uses (`accounts[0]`),
 * read when the token is needed rather than when the client was made.
 */
export async function acquireGraphToken(
  instance: IPublicClientApplication,
  account: AccountInfo | null = instance.getAllAccounts()[0] ?? null,
): Promise<string> {
  if (!account) {
    throw new Error('No authenticated account. Call login() first.');
  }
  // Silent first, then one popup shared by every caller on the page (13.1-13, ISS-240). A popup, not a redirect:
  // a redirect navigates away and loses page state.
  return acquireToken(instance, { ...graphScopes, account });
}

/**
 * Central auth hook wrapping MSAL React.
 *
 * Provides login/logout, token acquisition for Graph API,
 * role detection, and loading state.
 */
export function useAuth() {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const isLoading = inProgress !== InteractionStatus.None;

  const account = accounts[0] ?? null;
  const roles: AppRole[] = getRolesFromAccount(account);

  const user: AuthUser | null = account
    ? {
        name: account.name ?? '',
        email: account.username ?? '',
        roles,
        isOversight: roles.includes('Oversight'),
        isCareManager: roles.includes('CareManager'),
        isHRWHS: roles.includes('HRWHS'),
        isFinance: roles.includes('Finance'),
        isOfficeUser: roles.some((r) => OFFICE_ROLES.includes(r)),
        accountId: account.homeAccountId,
      }
    : null;

  /** Sign in via redirect — navigates to Microsoft login and back */
  async function login(): Promise<void> {
    await instance.loginRedirect(loginRequest);
  }

  /** Sign out via redirect */
  async function logout(): Promise<void> {
    await instance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin,
    });
  }

  /**
   * Acquire an access token for Microsoft Graph API.
   * Tries silent acquisition first; falls back to popup if interaction is required.
   */
  function getGraphToken(): Promise<string> {
    return acquireGraphToken(instance, account);
  }

  return {
    isAuthenticated,
    isLoading,
    user,
    roles,
    isOversight: user?.isOversight ?? false,
    isCareManager: user?.isCareManager ?? false,
    isHRWHS: user?.isHRWHS ?? false,
    isFinance: user?.isFinance ?? false,
    isOfficeUser: user?.isOfficeUser ?? false,
    login,
    logout,
    getGraphToken,
  };
}
